import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Slider from '@mui/material/Slider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import Divider from '@mui/material/Divider';
import { fetchRentals } from '../services/api';

const RentalSelector = ({ onRentalSelected, profile = 'timur' }) => {
  const [rentals, setRentals] = useState([]);
  const [selectedRentalId, setSelectedRentalId] = useState('');
  const [minStops, setMinStops] = useState('');
  const [maxStops, setMaxStops] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // New advanced settings
  const [startRadius, setStartRadius] = useState(300);
  const [endRadius, setEndRadius] = useState(300);
  const [minStartStops, setMinStartStops] = useState(0);
  const [maxStartStops, setMaxStartStops] = useState(20);
  const [minEndStops, setMinEndStops] = useState(0);
  const [maxEndStops, setMaxEndStops] = useState(20);
  const [advancedMode, setAdvancedMode] = useState(false);
  
  // Extract profile from props - this will be passed to the API call
  const currentProfile = profile === undefined ? 'timur' : profile;

  // Fetch rentals on component mount or when profile changes
  useEffect(() => {
    const loadRentals = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Pass the profile to the API call
        const rentalData = await fetchRentals(currentProfile);
        setRentals(rentalData);
        console.log(`Loaded ${rentalData.length} rentals for profile: ${currentProfile}`);
      } catch (err) {
        setError('Failed to load rentals. Please try again later.');
        console.error('Error loading rentals:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadRentals();
  }, [currentProfile]);

  const handleRentalChange = (event) => {
    setSelectedRentalId(event.target.value);
  };

  const handleMinStopsChange = (event) => {
    setMinStops(event.target.value);
  };

  const handleMaxStopsChange = (event) => {
    setMaxStops(event.target.value);
  };
  
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedRentalId) return;
    
    const selectedRental = rentals.find(rental => rental.id === selectedRentalId);
    
    // Create options object with all parameters
    const options = {};
    
    // If using legacy min/max stops and not in advanced mode
    if (!advancedMode) {
      if (minStops !== '') {
        options.minStops = parseInt(minStops);
      }
      
      if (maxStops !== '') {
        options.maxStops = parseInt(maxStops);
      }
    } else {
      // Use advanced settings parameters
      options.start_radius = startRadius;
      options.end_radius = endRadius;
      options.min_start_stops = minStartStops;
      options.max_start_stops = maxStartStops;
      options.min_end_stops = minEndStops;
      options.max_end_stops = maxEndStops;
    }
    
    // Pass the rental and options to the parent component
    onRentalSelected(selectedRental, options);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h6" gutterBottom>
        Select a Rental
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ my: 2 }}>
          {error}
        </Typography>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="rental-search"
              label="Search Rentals"
              value={searchTerm}
              onChange={handleSearchChange}
              margin="normal"
              placeholder="Search by driver, license plate, location..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="rental-select-label">Select Rental</InputLabel>
              <Select
                labelId="rental-select-label"
                id="rental-select"
                value={selectedRentalId}
                label="Select Rental"
                onChange={handleRentalChange}
                startAdornment={<DirectionsCarIcon sx={{ mr: 1, ml: -0.5 }} />}
                required
              >
                {rentals
                  .filter(rental => 
                    searchTerm === '' ||
                    rental.driver.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    rental.license_plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    rental.pickup_location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    rental.dropoff_location.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((rental) => (
                    <MenuItem key={rental.id} value={rental.id}>
                      {rental.driver} - {rental.license_plate} - {rental.pickup_location} to {rental.dropoff_location} ({rental.total_kilometers.toFixed(1)} km)
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
          
          {selectedRentalId && (
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Selected rental details:
              </Typography>
              {(() => {
                const rental = rentals.find(r => r.id === selectedRentalId);
                return rental ? (
                  <Box sx={{ pl: 2, borderLeft: '3px solid #1976d2', mb: 2 }}>
                    <Typography variant="body2"><strong>Driver:</strong> {rental.driver}</Typography>
                    <Typography variant="body2"><strong>License Plate:</strong> {rental.license_plate}</Typography>
                    <Typography variant="body2"><strong>From:</strong> {rental.pickup_location}</Typography>
                    <Typography variant="body2"><strong>To:</strong> {rental.dropoff_location}</Typography>
                    <Typography variant="body2"><strong>Distance:</strong> {rental.total_kilometers.toFixed(1)} km</Typography>
                    <Typography variant="body2"><strong>Dates:</strong> {rental.start_date} to {rental.end_date}</Typography>
                  </Box>
                ) : null;
              })()}
            </Grid>
          )}
          
          {!advancedMode ? (
            // Basic mode - simple min/max stops
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="minStopsRental"
                  label="Min Stops"
                  type="number"
                  value={minStops}
                  onChange={handleMinStopsChange}
                  helperText="Optional minimum number of stops"
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="maxStopsRental"
                  label="Max Stops"
                  type="number"
                  value={maxStops}
                  onChange={handleMaxStopsChange}
                  helperText="Optional maximum number of stops"
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
            </>
          ) : (
            // Advanced mode - configurable start/end stops and radius
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="advanced-panel-content"
                  id="advanced-panel-header"
                >
                  <TuneIcon sx={{ mr: 1 }} />
                  <Typography>Advanced Stop Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    {/* Start Location Settings */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" color="primary" gutterBottom>
                        Start Location Settings
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography id="start-radius-slider" gutterBottom>
                        Search Radius: {startRadius} km
                      </Typography>
                      <Slider
                        value={startRadius}
                        onChange={(e, newValue) => setStartRadius(newValue)}
                        aria-labelledby="start-radius-slider"
                        valueLabelDisplay="auto"
                        step={50}
                        min={50}
                        max={1000}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        id="minStartStops"
                        label="Min Stops"
                        type="number"
                        value={minStartStops}
                        onChange={(e) => setMinStartStops(parseInt(e.target.value) || 0)}
                        InputProps={{
                          inputProps: { min: 0 }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        id="maxStartStops"
                        label="Max Stops"
                        type="number"
                        value={maxStartStops}
                        onChange={(e) => setMaxStartStops(parseInt(e.target.value) || 0)}
                        InputProps={{
                          inputProps: { min: 0 }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    
                    {/* End Location Settings */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" color="secondary" gutterBottom>
                        End Location Settings
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography id="end-radius-slider" gutterBottom>
                        Search Radius: {endRadius} km
                      </Typography>
                      <Slider
                        value={endRadius}
                        onChange={(e, newValue) => setEndRadius(newValue)}
                        aria-labelledby="end-radius-slider"
                        valueLabelDisplay="auto"
                        step={50}
                        min={50}
                        max={1000}
                        color="secondary"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        id="minEndStops"
                        label="Min Stops"
                        type="number"
                        value={minEndStops}
                        onChange={(e) => setMinEndStops(parseInt(e.target.value) || 0)}
                        InputProps={{
                          inputProps: { min: 0 }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        id="maxEndStops"
                        label="Max Stops"
                        type="number"
                        value={maxEndStops}
                        onChange={(e) => setMaxEndStops(parseInt(e.target.value) || 0)}
                        InputProps={{
                          inputProps: { min: 0 }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        These settings control how many stops are generated near start and end locations. 
                        Increase radius (up to 1000km) to find more stops. You can set up to 30+ stops 
                        per location for very complex trips. The system will optimize for the total trip distance.
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              size="large"
              disabled={!selectedRentalId || loading}
            >
              Generate Trip
            </Button>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default RentalSelector;